import { useMemo, useState } from "react";
import { Notify, toast } from "@/components/ui/notify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { api, getFriendlyErrorMessage } from "@/lib/api-client";

interface SensorListItem {
  id: string;
  code: string;
  name: string;
  state: "AVAILABLE" | "LINKED" | "NO_READING" | "INCONSISTENT";
  status: "ONLINE" | "OFFLINE" | "NO_READING" | "INCONSISTENT";
  dataType: string;
}

interface Props {
  parameterId: string | null;
  open: boolean;
  onClose: () => void;
  onLinkOverride?: (sensorId: string) => Promise<void>;
  /**
   * Sensores a ocultar da lista mesmo que estejam AVAILABLE no servidor.
   * Em rascunho, os vínculos só vivem no store local (o sensor permanece
   * AVAILABLE no backend), então passamos aqui os já vinculados a qualquer
   * máquina do rascunho para não oferecê-los de novo.
   */
  excludeSensorIds?: string[];
}

function useAvailableSensors() {
  return useQuery<SensorListItem[]>({
    queryKey: ["sensors", "available"],
    queryFn: () =>
      api
        .get<SensorListItem[]>("/api/v1/sensors")
        .then((all) => all.filter((s) => s.state === "AVAILABLE")),
    staleTime: 30_000,
  });
}

function useLinkSensor(parameterId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sensorId: string) =>
      api.post(`/api/v1/parameters/${parameterId}/sensor-link`, { sensorId }),
    meta: { skipErrorToast: true },
    onSuccess: () => {
      // `["machine-sheet"]` era chave morta; ["lines"]/["parameters"] (prefixos)
      // cobrem ["lines",lineId,"machines"] e ["parameters",machineId] (badge do parâmetro).
      void qc.invalidateQueries({ queryKey: ["sensors"] });
      void qc.invalidateQueries({ queryKey: ["lines"] });
      void qc.invalidateQueries({ queryKey: ["parameters"] });
    },
  });
}

export function LinkSensorDialog({
  parameterId,
  open,
  onClose,
  onLinkOverride,
  excludeSensorIds,
}: Props) {
  const [selected, setSelected] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const sensorsQuery = useAvailableSensors();
  const { data: allSensors = [], isLoading } = sensorsQuery;
  const link = useLinkSensor(parameterId);

  const sensors = useMemo(() => {
    const excluded = new Set(excludeSensorIds ?? []);
    return allSensors.filter((s) => !excluded.has(s.id));
  }, [allSensors, excludeSensorIds]);

  if (!open || !parameterId) return null;

  async function submit() {
    if (!selected) {
      toast.error("Selecione um sensor.");
      return;
    }
    setSubmitting(true);
    try {
      if (onLinkOverride) {
        await onLinkOverride(selected);
      } else {
        await link.mutateAsync(selected);
        toast.success("Sensor vinculado.");
      }
      setSelected("");
      onClose();
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao vincular.", { description: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- backdrop overlay; click outside fecha via target check
    <div
      className="fixed inset-0 z-[60] flex animate-fade-in items-center justify-center bg-background/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Vincular sensor"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md animate-scale-in">
        <CardContent className="space-y-3 pt-6">
          <div>
            <h2 className="text-base font-semibold">Vincular sensor ao parâmetro</h2>
            <p className="text-xs text-muted-foreground">
              Apenas sensores com estado <strong>Disponível</strong> aparecem na lista. Após
              vincular, as leituras passam a alimentar este parâmetro.
            </p>
          </div>

          {sensorsQuery.isError ? (
            <Notify.QueryError
              variant="inline"
              error={sensorsQuery.error}
              onRetry={() => void sensorsQuery.refetch()}
              title="Não foi possível carregar os sensores disponíveis"
            />
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="link-sensor">Sensor disponível</Label>
              <Select
                id="link-sensor"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                disabled={isLoading || sensors.length === 0}
              >
                <option value="">
                  {isLoading
                    ? "Carregando…"
                    : sensors.length === 0
                      ? "Nenhum sensor disponível"
                      : "— Selecione —"}
                </option>
                {sensors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} · {s.name} ({s.dataType})
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => void submit()}
              disabled={submitting || link.isPending || !selected || sensorsQuery.isError}
            >
              {submitting || link.isPending ? "Vinculando…" : "Vincular"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export { UnlinkSensorDialog } from "./UnlinkSensorDialog";
